import React from "react";
import {
  FaTicketAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaHourglassEnd,
} from "react-icons/fa";

// ─── Skeleton ──────────────────────────────────────────────────────────────
const SkeletonStat = () => (
  <div className="flex animate-pulse items-center gap-3 rounded-xl border border-gray-100 bg-white p-4">
    <div className="h-10 w-10 rounded-lg bg-gray-100" />
    <div className="flex-1 space-y-2">
      <div className="h-3 w-20 rounded bg-gray-100" />
      <div className="h-6 w-10 rounded bg-gray-100" />
      <div className="h-3 w-16 rounded bg-gray-100" />
    </div>
  </div>
);

// ─── Stat Card ─────────────────────────────────────────────────────────────
const StatCard = ({
  icon: Icon,
  label,
  value,
  iconBg,
  iconColor,
  valueColor,
  borderColor,
  subLabel,
}) => (
  <div
    className={`flex items-start gap-3 rounded-xl border ${
      borderColor || "border-gray-100"
    } bg-white p-4 transition-all hover:-translate-y-0.5 hover:shadow-sm`}
  >
    <div
      className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${iconBg}`}
    >
      <Icon className={`text-lg ${iconColor}`} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className={`text-2xl font-bold leading-tight ${valueColor}`}>
        {value ?? 0}
      </p>
      {subLabel && (
        <p className="mt-0.5 text-[11px] text-gray-400">{subLabel}</p>
      )}
    </div>
  </div>
);

// ─── Main Component ────────────────────────────────────────────────────────
const CouponStats = ({ stats, loading }) => {
  if (loading) {
    return (
      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SkeletonStat />
        <SkeletonStat />
        <SkeletonStat />
        <SkeletonStat />
      </div>
    );
  }

  // "Active" = status active AND not yet expired
  // "Expired" = expiryDate in the past (regardless of status)
  // "Inactive" = status inactive AND not expired (manually disabled)
  const expiredCount = stats?.expired ?? 0;
  const activeCount = stats?.active ?? 0; // backend: status=active + expiryDate > now
  const inactiveCount = stats?.inactive ?? 0; // backend: status=inactive + expiryDate > now
  const totalCount = stats?.total ?? 0;

  return (
    <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
      <StatCard
        icon={FaTicketAlt}
        label="Total Codes"
        value={totalCount}
        iconBg="bg-pink-50"
        iconColor="text-pink-600"
        valueColor="text-gray-800"
        borderColor="border-pink-100"
        subLabel="All time"
      />
      <StatCard
        icon={FaCheckCircle}
        label="Active"
        value={activeCount}
        iconBg="bg-emerald-50"
        iconColor="text-emerald-600"
        valueColor="text-emerald-700"
        borderColor="border-emerald-100"
        subLabel="Live & valid"
      />
      <StatCard
        icon={FaTimesCircle}
        label="Inactive"
        value={inactiveCount}
        iconBg="bg-gray-50"
        iconColor="text-gray-500"
        valueColor="text-gray-600"
        borderColor="border-gray-200"
        subLabel="Manually disabled"
      />
      <StatCard
        icon={FaHourglassEnd}
        label="Expired"
        value={expiredCount}
        iconBg="bg-red-50"
        iconColor="text-red-500"
        valueColor="text-red-600"
        borderColor="border-red-100"
        subLabel="Past expiry date"
      />
    </div>
  );
};

export default CouponStats;
